import { isApiError } from '@/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useFolderMutations } from '../../hooks';
import type { DocumentBreadcrumbItem, DocumentFolder } from '../../types';
import { getDocumentMutationErrorMessage } from '../../utils/documentErrors';
import { FolderAction } from './DocumentList.types';

interface UseFolderManagementOptions {
  companyId: string | null;
  folderId: string | undefined;
  trimmedSearch: string;
  breadcrumbItems: DocumentBreadcrumbItem[];
  folderActions : {
    isDeletingFolders: boolean;
    isMovingFolders: boolean;
    deleteFolders: (folders: DocumentFolder[]) => Promise<void>;
    moveFolders: (folders: DocumentFolder[], targetFolderId: string | null) => Promise<void>;
  }
  onNavigateToParent: (parentFolder: DocumentBreadcrumbItem, parentBreadcrumbs: DocumentBreadcrumbItem[]) => void;
  onNavigateToRoot: () => void;
}

/**
 * Owns all folder CRUD interactions: creating, renaming, deleting and moving folders.
 * Delegates cache mutations to useFolderMutations and orchestrates post-action navigation.
 */
export function useFolderManagement({
  companyId,
  folderId,
  trimmedSearch,
  breadcrumbItems,
  folderActions,
  onNavigateToParent,
  onNavigateToRoot,
}: UseFolderManagementOptions) {
  const { t } = useTranslation();

  const [isCreateFolderFormOpen, setIsCreateFolderFormOpen] = useState(false);
  const [createFolderName, setCreateFolderName] = useState('');
  const [createFolderError, setCreateFolderError] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [folderPendingDelete, setFolderPendingDelete] = useState<DocumentFolder | null>(null);
  const [folderPendingMove, setFolderPendingMove] = useState<DocumentFolder | null>(null);

  const createFolderInputRef = useRef<HTMLInputElement>(null);

  const { createFolderMutation, renameFolderMutation } =
    useFolderMutations({ companyId, currentFolderId: folderId, search: trimmedSearch || undefined });

  const { isDeletingFolders, isMovingFolders, deleteFolders, moveFolders } = folderActions;

  const openCreateFolderForm = useCallback(() => {
    setIsCreateFolderFormOpen(true);
    setCreateFolderError('');
  }, []);

  const closeCreateFolderForm = useCallback(() => {
    setIsCreateFolderFormOpen(false);
    setCreateFolderName('');
    setCreateFolderError('');
  }, []);

  const beginFolderRename = useCallback((folder: DocumentFolder) => {
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
    setCreateFolderError('');
  }, []);

  const cancelFolderRename = useCallback(() => {
    setEditingFolderId(null);
    setEditingFolderName('');
  }, []);

  const handleCreateFolderNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCreateFolderName(event.target.value);
      if (createFolderError) setCreateFolderError('');
    },
    [createFolderError]
  );

  /** Validates the new folder name and calls the create mutation. */
  const handleCreateFolderSubmit = useCallback(async () => {
    const trimmedName = createFolderName.trim();
    const nameIsEmpty = !trimmedName;

    if (nameIsEmpty) {
      setCreateFolderError(t('documents.folderForm.nameRequired'));
      return;
    }

    setCreateFolderError('');

    try {
      await createFolderMutation.mutateAsync({ name: trimmedName, parentId: folderId ?? null });
      closeCreateFolderForm();
    } catch (error) {
      const message = getDocumentMutationErrorMessage(error, t);
      toast.error(message);
      const isDuplicateName = isApiError(error) && error.status === 400;
      if (isDuplicateName) setCreateFolderError(message);
    }
  }, [closeCreateFolderForm, createFolderMutation, createFolderName, folderId, t]);

  const handleCreateFolderKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') { event.preventDefault(); void handleCreateFolderSubmit(); }
      else if (event.key === 'Escape') { event.preventDefault(); closeCreateFolderForm(); }
    },
    [handleCreateFolderSubmit, closeCreateFolderForm]
  );

  /** Submits the rename if the name changed; aborts silently when unchanged. */
  const handleFolderRenameSubmit = useCallback(
    async (folder: DocumentFolder) => {
      const trimmedName = editingFolderName.trim();
      const nameIsEmpty = !trimmedName;
      const nameIsUnchanged = trimmedName === folder.name;

      if (nameIsEmpty) {
        toast.error(t('documents.folderForm.nameRequired'));
        cancelFolderRename();
        return;
      }

      if (nameIsUnchanged) {
        cancelFolderRename();
        return;
      }

      try {
        await renameFolderMutation.mutateAsync({ folderId: folder.id, name: trimmedName });
      } catch (error) {
        toast.error(getDocumentMutationErrorMessage(error, t));
      } finally {
        cancelFolderRename();
      }
    },
    [cancelFolderRename, editingFolderName, renameFolderMutation, t]
  );

  /**
   * Deletes the folder awaiting confirmation. When the deleted folder is the
   * one currently open, navigates back to its parent (or root).
   */
  const handleFolderDeleteConfirm = useCallback(async () => {
    if (!folderPendingDelete) return;

    const folderToDelete = folderPendingDelete;
    const deletingCurrentlyOpenFolder = folderId === folderToDelete.id;

    await deleteFolders([folderToDelete]);
    setFolderPendingDelete(null);

    if (deletingCurrentlyOpenFolder) {
      const parentBreadcrumbs = breadcrumbItems.slice(0, -1);
      const parentFolder = parentBreadcrumbs[parentBreadcrumbs.length - 1];

      if (parentFolder) {
        onNavigateToParent(parentFolder, parentBreadcrumbs);
      } else {
        onNavigateToRoot();
      }
    }
  }, [breadcrumbItems, deleteFolders, folderId, folderPendingDelete, onNavigateToParent, onNavigateToRoot]);

  /** Moves the folder to the selected target; skips the API call when dropped on its current parent. */
  const handleFolderMoveSelect = useCallback(
    async (targetFolderId: string | null) => {
      if (!folderPendingMove) return;
      await moveFolders([folderPendingMove], targetFolderId);
      setFolderPendingMove(null);
    },
    [folderPendingMove, moveFolders]
  );

  /** Resolves the 'root' sentinel to null before delegating to handleFolderMoveSelect. */
  const handleFolderMoveConfirm = useCallback(
    (targetFolder: { id: string | null }) => {
      const targetFolderId = targetFolder.id === 'root' ? null : targetFolder.id;
      void handleFolderMoveSelect(targetFolderId);
    },
    [handleFolderMoveSelect]
  );

  /** Routes folder row-action labels to the corresponding handler. */
  const handleFolderAction = useCallback(
    (actionLabel: FolderAction, folder: DocumentFolder) => {
      switch (actionLabel) {
        case FolderAction.Rename:
          beginFolderRename(folder);
          break;
        case FolderAction.Move:
          cancelFolderRename();
          setFolderPendingMove(folder);
          break;
        case FolderAction.Delete:
          cancelFolderRename();
          setFolderPendingDelete(folder);
          break;
      }
    },
    [beginFolderRename, cancelFolderRename, t]
  );

  // Auto-focus the folder name input as soon as the create-folder form appears
  useEffect(() => {
    if (isCreateFolderFormOpen) createFolderInputRef.current?.focus();
  }, [isCreateFolderFormOpen]);

  return {
    // create-folder form state
    isCreateFolderFormOpen,
    createFolderName,
    createFolderError,
    createFolderInputRef,
    createFolderMutation,
    openCreateFolderForm,
    closeCreateFolderForm,
    handleCreateFolderNameChange,
    handleCreateFolderKeyDown,
    handleCreateFolderSubmit,
    // rename state
    editingFolderId,
    editingFolderName,
    setEditingFolderName,
    renameFolderMutation,
    beginFolderRename,
    cancelFolderRename,
    handleFolderRenameSubmit,
    // delete state
    folderPendingDelete,
    setFolderPendingDelete,
    isDeletingFolders,
    handleFolderDeleteConfirm,
    // move state
    folderPendingMove,
    setFolderPendingMove,
    isMovingFolders,
    handleFolderMoveConfirm,
    // shared
    handleFolderAction,
  };
}
